import BaseSelect, { components } from 'react-select';
import type { ControlProps } from 'react-select/lib/components/Control';
import type { NoticeProps } from 'react-select/lib/components/Menu';
import type { OptionProps } from 'react-select/lib/components/Option';
import type { Props } from 'react-select/lib/Select';

import * as styles from './styles';
import type { SvgIconProps } from '@material-ui/core';

import { CheckIcon } from '../icons';

import { colors, fonts } from '../styles';
import { CaptionError } from '../typography';

export interface ISelectOptionType {
  label?: string;
  value?: string;
  displayCheckIcon?: boolean;
  Icon?: JSX.Element | ((props: SvgIconProps) => JSX.Element);
  isDisabled?: boolean;
  key?: string;
}

export interface SelectProps extends Props<ISelectOptionType> {
  errorMessage?: string;
}

const getSelectBorderColor = (isFocused: boolean, errorMessage?: string) => {
  if (errorMessage) {
    return colors.error.default;
  }
  return isFocused ? colors.black : colors.gray.light;
};

const Control = (props: ControlProps<ISelectOptionType>) => {
  const { children, hasValue, getValue, ...otherProps } = props;

  // Today, this component only supports single selection
  const option: any = hasValue
    ? (getValue() as ISelectOptionType[])[0]
    : undefined;

  return (
    <components.Control hasValue={hasValue} getValue={getValue} {...otherProps}>
      <div className={styles.option}>{option?.Icon}</div>
      {children}
    </components.Control>
  );
};

const Option = (props: OptionProps<ISelectOptionType>) => {
  const { children, data, ...otherProps } = props;
  const { Icon, displayCheckIcon = true } = data as ISelectOptionType;

  return (
    <components.Option data={data} {...otherProps}>
      <>
        <div className={styles.option}>
          <>
            {Icon}
            {children}
          </>
        </div>
        {otherProps.isSelected && displayCheckIcon ? <CheckIcon /> : null}
      </>
    </components.Option>
  );
};

const NoOptionsMessage = (props: NoticeProps<ISelectOptionType>) => (
  <components.NoOptionsMessage {...props}>
    Nenhuma opção disponível
  </components.NoOptionsMessage>
);

export const Select = (props: SelectProps) => {
  const value = props.value as ISelectOptionType;
  const fixedValue = value && !value.value && !value.label ? null : value;
  const { errorMessage, ...otherProps } = props;

  return (
    <>
      <BaseSelect
        {...otherProps}
        value={fixedValue}
        components={{
          Control,
          IndicatorSeparator: () => null,
          Option,
          NoOptionsMessage,
          ...otherProps.components,
        }}
        styles={{
          valueContainer: (provided) => ({
            ...provided,
            padding: 0,
            fontWeight: fonts.weight.regular,
            color: colors.black,
          }),
          singleValue: (provided, { isDisabled }) => ({
            ...provided,
            color: isDisabled ? colors.gray.dark : colors.black,
            fontWeight: isDisabled ? fonts.weight.light : fonts.weight.regular,
            margin: 0,
          }),
          noOptionsMessage: (provided) => ({
            ...provided,
            fontWeight: fonts.weight.light,
            fontSize: fonts.size.small,
            color: colors.gray.dark,
          }),
          control: (provided, { isFocused, isDisabled }) => ({
            ...provided,
            height: 28,
            minHeight: 28,
            boxSizing: 'content-box',
            boxShadow: 'none',
            backgroundColor: isDisabled ? colors.gray.lightest : 'white',
            pointerEvents: 'auto',
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            borderRadius: 0,
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: getSelectBorderColor(isFocused, errorMessage),
            padding: '5px 9px',
            fontSize: fonts.size.small,
            /* needed to leave hover state empty, the default behaviour is to change the border color */
            '&:hover': {},
            '&:focus': {
              borderColor: colors.black,
              outline: 'none',
              boxShadow: `0 0 10px 0 ${colors.custom.gray20}`,
            },
          }),
          menu: (provided) => ({
            ...provided,
            boxShadow: `0 0 10px 0 ${colors.custom.gray20}`,
            marginTop: 0,
            borderRadius: 0,
            borderWidth: '0 1px 1px 1px',
            borderStyle: 'solid',
            borderColor: colors.black,
            padding: '5px 0',
            zIndex: 10,
          }),
          placeholder: (provided) => ({
            ...provided,
            color: colors.gray.dark,
            padding: 0,
            fontWeight: fonts.weight.light,
          }),
          option: (provided, { isDisabled }) => ({
            ...provided,
            position: 'relative',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: 'white',
            padding: '5px 9px',
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            color: isDisabled ? colors.custom.gray50 : colors.gray.dark,
            fontWeight: fonts.weight.light,
            fontSize: fonts.size.small,
            '&:hover': {
              backgroundColor: isDisabled
                ? 'white'
                : colors.custom.lightestGray50,
            },
            '&:not(:last-child)': {
              marginBottom: '5px',
            },
          }),
          dropdownIndicator: (provided) => ({
            ...provided,
            color: colors.gray.default,
            padding: 0,
            '&:hover': {
              color: colors.gray.default,
            },
          }),
        }}
      />
      {errorMessage && (
        <CaptionError className={styles.errorMessage}>
          {errorMessage}
        </CaptionError>
      )}
    </>
  );
};
